import { useCallback, useEffect, useState } from "react";
import { fetchTwitchStreamStatus, TwitchStreamStatus } from "../services/twitchServices";

const TWITCH_POLL_MS = 60_000;
const DEFAULT_CHANNEL = "finnhouse";

export const useTwitchStream = (enabled: boolean, channel?: string | null) => {
  const [stream, setStream] = useState<TwitchStreamStatus | null>(null);
  const [loading, setLoading] = useState(enabled);
  const normalizedChannel = channel?.trim() || null;

  const refresh = useCallback(async () => {
    if (!enabled) {
      return;
    }
    try {
      const status = await fetchTwitchStreamStatus(normalizedChannel);
      setStream(status);
    } catch (error) {
      console.error("Failed to fetch Twitch stream status:", error);
      setStream({
        live: false,
        configured: false,
        login: normalizedChannel ?? DEFAULT_CHANNEL,
      });
    } finally {
      setLoading(false);
    }
  }, [enabled, normalizedChannel]);

  useEffect(() => {
    if (!enabled) {
      setStream(null);
      setLoading(false);
      return;
    }

    let lastRefreshAt = 0;

    const runRefresh = () => {
      lastRefreshAt = Date.now();
      void refresh();
    };

    const maybeRefreshOnVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastRefreshAt < TWITCH_POLL_MS) return;
      runRefresh();
    };

    setLoading(true);
    runRefresh();

    const timer = window.setInterval(runRefresh, TWITCH_POLL_MS);
    document.addEventListener("visibilitychange", maybeRefreshOnVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", maybeRefreshOnVisible);
    };
  }, [enabled, refresh]);

  return { stream, loading, refresh };
};
